import React, { Component } from 'react';
import { withStyles  } from '@mui/styles';
import PropTypes from 'prop-types';
import { isEmpty, startCase, lowerCase } from 'lodash';
import moment from 'moment';
import { getChangeHeadline, getChangeVerb, getEntryKey, mergeCreationEntries, visibleChanges } from '../utils/changeLog';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMore from "@mui/material/SvgIcon/SvgIcon";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";

const styles = theme => ({
  root: {
    width: window.innerWidth - 200,
    padding: 30
  },
  head: {
    display: 'flex',
    flexFlow: 'wrap'
  },
  fullWidth: {
    width: '100%'
  },
  title: {
    flex: 1
  },
  field: {
    width: '100%',
    marginTop: 5,
    padding: 10,
    background: theme.palette.primary.light
  },
  value: {
    width: '100%',
    marginTop: 5,
    padding: 10,
    minHeight: 44
  },
  fieldFont: {
    color: theme.palette.primary.dark,
    fontWeight: 700
  },
  valueFont: {
    fontWeight: 400
  },
  card: {
    minWidth: window.innerWidth - 200,
    position: 'relative'
  },
  cardWrapper: {
    position: 'absolute',
    height:  window.innerHeight,
    top: 'calc(50% - 6%)',
    left: 'calc(50% - 6%)',
    padding: 30
  }
});

class ChangeLog extends Component {

  componentDidMount() {
    if (isEmpty(this.props.changeLog) || this.props.changeLog.orderId !== this.props.order.id) {
      this.props.onLoad();
    }
  }

  // replace object with string and format date before applying font
  fixValue = thing => {
    let res;
    if (typeof thing === 'object') {
      res = JSON.stringify(thing).replace(/,/g, ', ');
    } else if (typeof thing === 'number') {
      res = thing.toString();
    } else if (moment(thing, moment.ISO_8601, true).isValid()) {
      res = moment(thing).format('MMM D, YYYY');
    } else {
      res = thing;
    }
    return this.makeNice(res);
  };

  makeNice = text => {
    const { classes } = this.props;
    return (
      <Typography variant="subtitle1" className={classes.valueFont}>
        {text}
      </Typography>
    )
  };

  makeTitle = text => {
    const { classes } = this.props;
    return (
      <Typography variant="h6" className={classes.title}>
        {text}
      </Typography>
    )
  };

  render() {
    const { classes, changeLog, onClose, open, isFetchingLog, order } = this.props;
    const displayLog = mergeCreationEntries(changeLog, order);
    const entries = (displayLog && displayLog.changes) || [];

    return (
      <div>
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
        >
          {isFetchingLog && (
            <div className={classes.card}>
              <div className={classes.cardWrapper}>
                <CircularProgress className={classes.progress} color="primary" />
                <Typography className={classes.text} color="primary">
                  Fetching change log for this order
                </Typography>
              </div>

            </div>
          ) }
          {!isFetchingLog && (
            <div className={classes.root}>
              <div className={classes.head}>
                {this.makeTitle(`IO Change Log for ${displayLog ? displayLog.orderId : '' }`)}
                <div>
                  <IconButton onClick={onClose} size="large">
                    <Close />
                  </IconButton>
                </div>
              </div>

              <Divider />

              {entries.length > 0 ? (
                <div>
                  {entries.map((cl, index) => {
                    const fieldChanges = visibleChanges(cl);
                    return (
                      <Accordion key={getEntryKey(cl, index)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1" className={classes.valueFont}>
                            {moment(cl.created).format('MMM D, YYYY HH:MM')}
                            {` ${getChangeHeadline(cl)}`}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <div className={classes.fullWidth}>
                            {fieldChanges.length > 0 ? (
                              <Grid
                                container
                                spacing={3}
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Grid item xs={4}>{this.makeTitle('Field Name')}</Grid>
                                <Grid item xs={4}>{this.makeTitle('Old Values')}</Grid>
                                <Grid item xs={4}>{this.makeTitle('New Values')}</Grid>
                              </Grid>
                            ) : (
                              this.makeNice(getChangeVerb(cl) === 'created' ? 'Initial creation' : 'No field-level changes')
                            )}
                            {fieldChanges.map(clc => {
                              return (
                                <div key={clc.prop} className={classes.value}>
                                  <Grid
                                    container
                                    spacing={3}
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Grid item xs={4} className={classes.field}>
                                      {this.makeNice(startCase(lowerCase(clc.prop)))}
                                    </Grid>
                                    <Grid item xs={4}>{this.fixValue(clc.oldVal)}</Grid>
                                    <Grid item xs={4}>{this.fixValue(clc.newVal)}</Grid>
                                  </Grid>
                                </div>
                              )
                            })
                            }
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    )
                  })}
                </div>
            ) : (
              <Typography variant="subtitle1">No change log items for this order</Typography>
            )}
            </div>
)
          }
        </Drawer>
      </div>
    );
  }
}

ChangeLog.defaultProps = {
  changeLog: null
};

ChangeLog.propTypes = {
  classes: PropTypes.object.isRequired,
  changeLog: PropTypes.object,
  order: PropTypes.object.isRequired
};

export default withStyles(styles)(ChangeLog);
